import React, { createContext, useState, useContext } from 'react';
import { useAuth } from 'oidc-react';

// --- Types ---

interface GroupMaintenanceSchedule {
  scheduleTitle: string;
  scheduleDescription: string;
  startDateTime: string;
  endDateTime: string;
  frequency: string;
  interval: number;
  timeWindowStart: string;
  timeWindowEnd: string;
  daysOfWeek: string[];
  daysOfMonth: string[];
  isRecurring: boolean;
  isActive: boolean;
  isDefault: boolean;
}

interface GroupAgentRebootDelayConfiguration {
  delayRebootByUnit: string;
  delayRebootValue: number;
}

interface CreateGroupData {
  TenantGroupId?: string;
  tenantGroupName: string;
  description: string;
  timezone: string;
  isActive: boolean;
  tenantAgentIds: string[];
  groupMaintenanceSchedule: GroupMaintenanceSchedule[];
  groupAgentRebootDelayConfiguration: GroupAgentRebootDelayConfiguration;
}

interface CreateGroupContextType {
  CreateGroupData: CreateGroupData;
  setCreateGroupData: React.Dispatch<React.SetStateAction<CreateGroupData>>;
  resetCreateGroupData: () => void; // 👈 add reset function
}

// --- Context ---

const CreateGroupContext = createContext<CreateGroupContextType | undefined>(undefined);

// --- Initial State Factory ---

const getInitialCreateGroupState = (): CreateGroupData => {
  const now = new Date();
  const tenYearsLater = new Date();
  tenYearsLater.setFullYear(now.getFullYear() + 10);

  return {
    tenantGroupName: '',
    description: '',
    timezone: 'UTC+05:30',
    isActive: true,
    tenantAgentIds: [],
    groupMaintenanceSchedule: [
      {
        scheduleTitle: '',
        scheduleDescription: '',
        startDateTime: now.toISOString(),
        endDateTime: tenYearsLater.toISOString(),
        frequency: '',
        interval: 1,
        timeWindowStart: '',
        timeWindowEnd: '',
        daysOfWeek: [],
        daysOfMonth: [],
        isRecurring: true,
        isActive: true,
        isDefault: true,
      },
    ],
    groupAgentRebootDelayConfiguration: {
      delayRebootByUnit: 'Minutes',
      delayRebootValue: 60,
    },
  };
};

// --- Provider ---

export const CreateGroupProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth(); // Currently unused, but available if needed

  const [CreateGroupData, setCreateGroupData] = useState<CreateGroupData>(getInitialCreateGroupState());

  // Reset function
  const resetCreateGroupData = () => {
    setCreateGroupData(getInitialCreateGroupState());
  };

  return (
    <CreateGroupContext.Provider value={{ CreateGroupData, setCreateGroupData, resetCreateGroupData }}>
      {children}
    </CreateGroupContext.Provider>
  );
};

// --- Hook ---

export const useCreateGroup = () => {
  const context = useContext(CreateGroupContext);
  if (!context) {
    throw new Error('useCreateGroup must be used within a CreateGroupProvider');
  }
  return context;
};
